import { Cartao, Ficha, TituloDeSecao } from './base';
import { data as formatarData, moeda, plural } from '../util/formato';

interface ParcelaDoHistorico {
  readonly numero: number;
  readonly valor: number;
  readonly status: string;
  readonly pagamento: { valorPago: number; dataPagamento: string } | null;
}

export interface AcordoAnterior {
  readonly id: string;
  readonly status: string;
  readonly valorTotal: number;
  readonly quantidadeParcelas: number;
  readonly totalPago: number;
  readonly parcelas: readonly ParcelaDoHistorico[];
}

const COMO_TERMINOU: Record<string, { rotulo: string; cor: string }> = {
  RENEGOCIADO: { rotulo: 'Renegociado', cor: 'var(--acento)' },
  ROMPIDO: { rotulo: 'Rompido', cor: 'var(--color-vencido)' },
  QUITADO: { rotulo: 'Quitado', cor: 'var(--color-recebido)' },
  CANCELADO: { rotulo: 'Cancelado', cor: 'var(--texto-tenue)' },
};

/**
 * Os acordos que já saíram de cena, e o que foi pago em cada um.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUE ISTO PRECISA APARECER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A ficha mostrava um acordo só — o ativo. Renegociar criava um novo, e o
 * anterior sumia da tela junto com as parcelas que a pessoa tinha pagado: R$ 130
 * quitados em março deixavam de existir aos olhos de quem abrisse a ficha.
 *
 * Num desentendimento com o devedor, é essa linha que responde "eu paguei, sim".
 * Ela não pode depender de alguém lembrar de procurar no histórico.
 *
 * SÓ AS PARCELAS PAGAS SÃO LISTADAS. As canceladas foram reescritas no acordo
 * seguinte; repeti-las aqui mostraria a mesma dívida duas vezes na mesma tela.
 */
export function AcordosAnteriores({ acordos }: { acordos: readonly AcordoAnterior[] }) {
  const anteriores = acordos.filter((a) => a.status !== 'ATIVO');
  if (anteriores.length === 0) return null;

  return (
    <Cartao semPadding>
      <div className="p-5 pb-3">
        <TituloDeSecao auxiliar="o que foi pago continua valendo">
          {anteriores.length > 1 ? 'Acordos anteriores' : 'Acordo anterior'}
        </TituloDeSecao>
      </div>

      <div className="border-t">
        {anteriores.map((acordo) => {
          const pagas = acordo.parcelas.filter((p) => p.pagamento);
          const desfecho = COMO_TERMINOU[acordo.status] ?? {
            rotulo: acordo.status.toLowerCase(),
            cor: 'var(--texto-tenue)',
          };

          return (
            <div key={acordo.id} className="border-b px-5 py-4 last:border-0">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="text-sm text-[color:var(--texto)]">
                  {moeda(acordo.valorTotal)} em {acordo.quantidadeParcelas}x
                </span>
                <Ficha cor={desfecho.cor}>{desfecho.rotulo}</Ficha>
              </div>

              {pagas.length === 0 ? (
                <p className="mt-1.5 text-xs text-[color:var(--texto-tenue)]">
                  Nenhuma parcela chegou a ser paga neste acordo.
                </p>
              ) : (
                <>
                  <p className="mt-1.5 text-xs text-[color:var(--texto-suave)]">
                    {plural(pagas.length, 'parcela paga', 'parcelas pagas')} ·{' '}
                    <strong
                      className="tabular font-semibold"
                      style={{ color: 'var(--color-recebido)' }}
                    >
                      {moeda(acordo.totalPago)}
                    </strong>{' '}
                    que o devedor já quitou
                  </p>

                  <ul className="mt-2 space-y-1">
                    {pagas.map((p) => (
                      <li
                        key={p.numero}
                        className="flex flex-wrap items-baseline gap-x-3 text-xs text-[color:var(--texto-suave)]"
                      >
                        <span className="w-20 shrink-0">
                          {p.numero === 0 ? 'Entrada' : `Parcela ${p.numero}`}
                        </span>
                        <span className="tabular">{moeda(p.pagamento!.valorPago)}</span>
                        <span className="text-[color:var(--texto-tenue)]">
                          pago em {formatarData(p.pagamento!.dataPagamento)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Cartao>
  );
}
